import Datatable from "./datatable_controller";
import { post } from "@rails/request.js";
import * as bootstrap from "bootstrap";

export default class extends Datatable {
  static targets = [
    'filter', 'sourceFilter',
    // Bulk selection UI
    'toolbar', 'bulkBar', 'bulkCount', 'selectAllBanner', 'headerCheckbox',
    'addToGroupToggle', 'addPanel', 'addPanelSearch', 'addPanelList',
    'createGroupToggle', 'createGroupForm', 'createGroupName', 'createGroupError', 'createGroupSubmit',
    'removeToggle', 'removePanel', 'removePanelList', 'exportBtn',
    'bulkDeleteTitle', 'bulkDeleteSubmit', 'bulkRemoveTitle', 'bulkRemoveSubmit'
  ]
  static values = {
    groupId: String,
    bulkAddUrl: String,
    bulkRemoveUrl: String,
    bulkSummaryUrl: String,
    bulkExportUrl: String,
    bulkDestroyUrl: String,
    bulkI18n: Object
  }

  connect() {
    super.connect();
    // Handle generic filter changes (like order controller)
    $(this.filterTargets).on('select2:select select2:unselect', this.filterSelected.bind(this));
    // Plain (non-select2) filter selects/inputs in the FILTERS panel also redraw
    // on native change/input.
    this.filterTargets.forEach((el) => el.addEventListener('change', this.filterSelected.bind(this)));
    // Handle source filter changes (legacy)
    if (this.hasSourceFilterTarget) {
      this.sourceFilterTarget.addEventListener('change', this.filterSelected.bind(this));
    }

    // ---- Bulk selection state ----
    // Explicit row selection (ids). Cleared on every user-driven draw (page,
    // search, filter change) so the selection never includes rows the user
    // can't see. Programmatic redraws after a bulk action set
    // preserveNextDraw to keep (or narrow) the selection instead.
    this.selected = new Set();
    this.selectAllMode = false;
    this.preserveNextDraw = false;
    this.pendingRemoveGroup = null;

    if (this.hasBulkBarTarget) {
      this.tableTarget.addEventListener('change', this.onRowCheckboxChange.bind(this));
      this.tableTarget.addEventListener('click', this.onCellClick.bind(this));
      $(this.tableTarget).on('draw.dt', this.onDraw.bind(this));
      this.onDocumentClick = this.closePanelsOnOutsideClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      this.onKeydown = this.handleEscape.bind(this);
      document.addEventListener('keydown', this.onKeydown);
    }
  }

  disconnect() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    super.disconnect();
  }

  filterSelected(_event) {
    this.dataTable.draw()
  }

  manipulateData(data) {
    this.filterTargets.map((filter) => {
      data[filter.name] = $(filter).val()
    })

    // Add group_id filter if present
    if (this.groupIdValue && this.groupIdValue !== '') {
      data.group_id = this.groupIdValue;
    }

    // The FILTERS panel + sidebar nav set filters via URL params. Read them
    // straight from the URL so they apply on load and survive across the
    // side-nav/content DOM boundary.
    const qs = new URLSearchParams(window.location.search);
    ["event_id", "ticket_id", "source", "date_added"].forEach((key) => {
      const value = qs.get(key);
      if (value != null && value !== "") data[key] = value;
    });

    return data
  }

  get columns() {
    return [
      { data: "select", orderable: false, className: "bulk-select-col" },
      { data: "contact"},
      { data: "source"},
      { data: "groups"},
      { data: "added"},
      { data: "status"},
      { data: "actions"},
    ];
  }

  get searching() {
    return true;
  }

  get ordering() {
    return false;
  }

  // ==== Bulk selection =====================================================

  t(key, vars = {}) {
    let template = this.bulkI18nValue[key] || key;
    Object.entries(vars).forEach(([k, v]) => {
      template = template.replaceAll(`%{${k}}`, v);
    });
    return template;
  }

  plural(base, count, vars = {}) {
    return this.t(count === 1 ? `${base}_one` : `${base}_other`, { count, ...vars });
  }

  get totalMatching() {
    return this.dataTable.page.info().recordsDisplay;
  }

  get pageCheckboxes() {
    return Array.from(this.tableTarget.querySelectorAll('tbody .bulk-select-checkbox'));
  }

  get selectionCount() {
    return this.selectAllMode ? this.totalMatching : this.selected.size;
  }

  // Generous hit area: clicking anywhere in the checkbox cell toggles it.
  onCellClick(event) {
    const cell = event.target.closest('.bulk-select-cell');
    if (!cell || event.target.matches('.bulk-select-checkbox')) return;
    const checkbox = cell.querySelector('.bulk-select-checkbox');
    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRowCheckboxChange(event) {
    const checkbox = event.target;
    if (!checkbox.matches('.bulk-select-checkbox')) return;
    if (checkbox.checked) {
      this.selected.add(checkbox.value);
    } else {
      this.selected.delete(checkbox.value);
      // Any manual uncheck narrows the selection below "everything matching".
      this.selectAllMode = false;
    }
    this.updateBulkUI();
  }

  toggleSelectPage(event) {
    const checked = event.target.checked;
    this.pageCheckboxes.forEach((box) => {
      box.checked = checked;
      if (checked) this.selected.add(box.value); else this.selected.delete(box.value);
    });
    if (!checked) this.selectAllMode = false;
    this.updateBulkUI();
  }

  enterSelectAllMode(event) {
    event.preventDefault();
    this.selectAllMode = true;
    this.updateBulkUI();
  }

  clearSelection() {
    this.selected.clear();
    this.selectAllMode = false;
    this.pageCheckboxes.forEach((box) => { box.checked = false; });
    this.closePanels();
    this.updateBulkUI();
  }

  // Every user-driven draw (pagination, search, filter) clears the selection —
  // a kept-but-invisible selection misleads users about what an action will
  // touch. Programmatic redraws after bulk actions re-apply instead.
  onDraw() {
    if (this.preserveNextDraw) {
      this.preserveNextDraw = false;
      // In select-all mode every (re)drawn row is part of the selection.
      this.pageCheckboxes.forEach((box) => {
        box.checked = this.selectAllMode || this.selected.has(box.value);
        if (this.selectAllMode) this.selected.add(box.value);
      });
    } else {
      this.selected.clear();
      this.selectAllMode = false;
      this.closePanels();
    }
    this.updateBulkUI();
  }

  updateBulkUI() {
    const count = this.selectionCount;
    const active = count > 0;
    this.bulkBarTarget.classList.toggle('d-none', !active);
    // The bar replaces the toolbar row (same slot, same min-height) so the
    // table doesn't jump vertically.
    this.toolbarTarget.classList.toggle('d-none', active);
    if (active) this.bulkCountTarget.textContent = this.plural('selected', count);

    // Header checkbox mirrors the visible page: checked / indeterminate / off.
    const boxes = this.pageCheckboxes;
    const checkedOnPage = boxes.filter((b) => b.checked).length;
    if (this.hasHeaderCheckboxTarget) {
      this.headerCheckboxTarget.checked = boxes.length > 0 && checkedOnPage === boxes.length;
      this.headerCheckboxTarget.indeterminate = checkedOnPage > 0 && checkedOnPage < boxes.length;
    }

    this.updateSelectAllBanner(boxes.length, checkedOnPage);
  }

  // "25 of 278 contacts selected. [Select all 278]" — shown when the whole
  // visible page is selected but more rows match; switches to "All 278
  // selected" once select-all mode is on.
  updateSelectAllBanner(pageCount, checkedOnPage) {
    const banner = this.selectAllBannerTarget;
    banner.innerHTML = '';
    const total = this.totalMatching;

    if (this.selectAllMode) {
      banner.classList.remove('d-none');
      banner.append(this.t('all_selected', { total }), ' ');
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'btn btn-link bulk-banner-link';
      clear.textContent = this.t('clear_selection');
      clear.addEventListener('click', () => this.clearSelection());
      banner.appendChild(clear);
    } else if (pageCount > 0 && checkedOnPage === pageCount && total > this.selected.size) {
      banner.classList.remove('d-none');
      banner.append(this.t('select_all_hint', { selected: this.selected.size, total }), ' ');
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'btn btn-link bulk-banner-link';
      link.textContent = this.t('select_all_link', { total });
      link.addEventListener('click', this.enterSelectAllMode.bind(this));
      banner.appendChild(link);
    } else {
      banner.classList.add('d-none');
    }
  }

  // Selection as request params: explicit ids, or select_all + the active
  // filters (mirrors manipulateData) so the server resolves the same rows.
  selectionParams() {
    if (!this.selectAllMode) return { contact_ids: Array.from(this.selected) };

    const params = { select_all: true };
    this.manipulateData(params);
    const search = this.dataTable.search();
    if (search) params.search = search;
    return params;
  }

  // ==== Panels (Add to Group / Remove from Group) ==========================

  toggleAddPanel() {
    const wasOpen = !this.addPanelTarget.classList.contains('d-none');
    this.closePanels();
    if (wasOpen) return;
    this.openPanel(this.addPanelTarget, this.addToGroupToggleTarget);
    this.loadGroupSummary().then((data) => {
      if (!data) return;
      this.renderAddPanel(data.groups);
    });
  }

  toggleRemovePanel() {
    const wasOpen = !this.removePanelTarget.classList.contains('d-none');
    this.closePanels();
    if (wasOpen) return;
    this.openPanel(this.removePanelTarget, this.removeToggleTarget);
    this.loadGroupSummary().then((data) => {
      if (!data) return;
      this.renderRemovePanel(data.groups, data.selected);
    });
  }

  openPanel(panel, toggle) {
    panel.classList.remove('d-none');
    toggle.setAttribute('aria-expanded', 'true');
    this.openToggle = toggle;
  }

  closePanels() {
    if (!this.hasAddPanelTarget) return;
    [this.addPanelTarget, this.removePanelTarget].forEach((panel) => panel.classList.add('d-none'));
    [this.addToGroupToggleTarget, this.removeToggleTarget].forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.collapseCreateGroup();
  }

  closePanelsOnOutsideClick(event) {
    if (event.target.closest('.bulk-dropdown')) return;
    this.closePanels();
  }

  // Escape closes an open panel and returns focus to its toggle; the create
  // form collapses back to its trigger first. Escape never clears the
  // selection — only the ✕ does.
  handleEscape(event) {
    if (event.key !== 'Escape') return;
    if (this.hasCreateGroupFormTarget && !this.createGroupFormTarget.classList.contains('d-none')
        && this.createGroupFormTarget.contains(document.activeElement)) {
      this.collapseCreateGroup();
      this.createGroupToggleTarget.focus();
      event.stopPropagation();
      return;
    }
    const anyOpen = !this.addPanelTarget.classList.contains('d-none')
      || !this.removePanelTarget.classList.contains('d-none');
    if (anyOpen) {
      const toggle = this.openToggle;
      this.closePanels();
      if (toggle) toggle.focus();
    }
  }

  async loadGroupSummary() {
    const response = await post(this.bulkSummaryUrlValue, {
      body: this.selectionParams(), contentType: 'application/json', responseKind: 'json'
    });
    if (!response.ok) {
      this.toast(this.t('action_failed'), 'error');
      this.closePanels();
      return null;
    }
    return response.json;
  }

  renderAddPanel(groups) {
    const list = this.addPanelListTarget;
    list.innerHTML = '';
    this.addPanelSearchTarget.classList.toggle('d-none', groups.length < 8);
    if (groups.length === 0) {
      list.appendChild(this.panelEmptyState(this.t('no_groups_yet')));
      return;
    }
    groups.forEach((group) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'bulk-panel-item';
      item.dataset.groupName = group.name.toLowerCase();
      const name = document.createElement('span');
      name.className = 'bulk-panel-item-name';
      name.textContent = group.name;
      const meta = document.createElement('span');
      meta.className = 'bulk-panel-item-meta';
      meta.textContent = this.plural('members', group.member_count);
      item.append(name, meta);
      item.addEventListener('click', () => this.bulkAddToGroup({ contact_group_id: group.id }));
      list.appendChild(item);
    });
  }

  renderRemovePanel(groups, selectedCount) {
    const list = this.removePanelListTarget;
    list.innerHTML = '';
    const affected = groups.filter((g) => g.affected > 0);
    if (affected.length === 0) {
      list.appendChild(this.panelEmptyState(this.t('no_groups_for_selection')));
      return;
    }
    affected.forEach((group) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'bulk-panel-item';
      const name = document.createElement('span');
      name.className = 'bulk-panel-item-name';
      name.textContent = group.name;
      const meta = document.createElement('span');
      meta.className = 'bulk-panel-item-meta';
      meta.textContent = this.t('affected_of_selected', { affected: group.affected, selected: selectedCount });
      item.append(name, meta);
      item.addEventListener('click', () => this.confirmBulkRemove(group));
      list.appendChild(item);
    });
  }

  panelEmptyState(message) {
    const empty = document.createElement('div');
    empty.className = 'bulk-panel-empty';
    empty.textContent = message;
    return empty;
  }

  filterAddPanel() {
    const query = this.addPanelSearchTarget.value.trim().toLowerCase();
    this.addPanelListTarget.querySelectorAll('.bulk-panel-item').forEach((item) => {
      item.classList.toggle('d-none', query !== '' && !item.dataset.groupName.includes(query));
    });
  }

  // ==== Inline "create new group" ==========================================

  expandCreateGroup() {
    this.createGroupToggleTarget.classList.add('d-none');
    this.createGroupFormTarget.classList.remove('d-none');
    this.createGroupNameTarget.focus();
  }

  collapseCreateGroup() {
    if (!this.hasCreateGroupFormTarget) return;
    this.createGroupFormTarget.classList.add('d-none');
    this.createGroupToggleTarget.classList.remove('d-none');
    this.createGroupNameTarget.value = '';
    this.hideCreateGroupError();
  }

  createGroupKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitCreateGroup();
    }
  }

  submitCreateGroup() {
    const name = this.createGroupNameTarget.value.trim();
    if (name === '') {
      this.showCreateGroupError(this.t('group_name_required'));
      return;
    }
    this.bulkAddToGroup({ new_group_name: name });
  }

  showCreateGroupError(message) {
    this.createGroupErrorTarget.textContent = message;
    this.createGroupErrorTarget.classList.remove('d-none');
  }

  hideCreateGroupError() {
    if (this.hasCreateGroupErrorTarget) this.createGroupErrorTarget.classList.add('d-none');
  }

  // ==== Bulk actions ========================================================

  async bulkAddToGroup(target) {
    this.hideCreateGroupError();
    const submitting = target.new_group_name && this.hasCreateGroupSubmitTarget;
    if (submitting) this.setBusy(this.createGroupSubmitTarget, true);
    try {
      const response = await post(this.bulkAddUrlValue, {
        body: { ...this.selectionParams(), ...target },
        contentType: 'application/json', responseKind: 'json'
      });
      const data = await response.json;
      if (response.ok) {
        this.closePanels();
        this.toast(data.message, 'success');
        this.refreshTablePreservingSelection();
        this.addToGroupToggleTarget.focus();
      } else if (data && data.error_field === 'new_group_name') {
        this.showCreateGroupError(data.message);
      } else {
        this.toast((data && data.message) || this.t('action_failed'), 'error');
      }
    } catch {
      this.toast(this.t('action_failed'), 'error');
    } finally {
      if (submitting) this.setBusy(this.createGroupSubmitTarget, false);
    }
  }

  confirmBulkRemove(group) {
    this.pendingRemoveGroup = group;
    this.closePanels();
    this.bulkRemoveTitleTarget.textContent =
      this.plural('confirm_remove_title', group.affected, { group: group.name });
    this.showModal('bulkRemoveFromGroupModal', this.removeToggleTarget);
  }

  async performBulkRemove() {
    const group = this.pendingRemoveGroup;
    if (!group) return;
    this.setBusy(this.bulkRemoveSubmitTarget, true);
    try {
      const response = await post(this.bulkRemoveUrlValue, {
        body: { ...this.selectionParams(), contact_group_id: group.id },
        contentType: 'application/json', responseKind: 'json'
      });
      const data = await response.json;
      if (response.ok) {
        this.hideModal('bulkRemoveFromGroupModal');
        this.toast(data.message, data.status === 'partial' ? 'error' : 'success');
        // Partial failure: narrow the selection to the contacts that couldn't
        // be updated so the user can see and retry them.
        if (data.status === 'partial') this.narrowSelectionTo(data.failed_ids);
        this.refreshTablePreservingSelection();
      } else {
        this.toast((data && data.message) || this.t('action_failed'), 'error');
      }
    } catch {
      this.toast(this.t('action_failed'), 'error');
    } finally {
      this.setBusy(this.bulkRemoveSubmitTarget, false);
      this.pendingRemoveGroup = null;
    }
  }

  async bulkExport() {
    this.setBusy(this.exportBtnTarget, true);
    try {
      const response = await post(this.bulkExportUrlValue, {
        body: this.selectionParams(), contentType: 'application/json'
      });
      if (!response.ok) {
        this.toast(this.t('action_failed'), 'error');
        return;
      }
      const blob = await response.response.blob();
      const disposition = response.response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = match ? match[1] : 'contacts-selected.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      this.toast(this.t('action_failed'), 'error');
    } finally {
      this.setBusy(this.exportBtnTarget, false);
    }
  }

  confirmBulkDelete() {
    this.bulkDeleteTitleTarget.textContent = this.plural('confirm_delete_title', this.selectionCount);
    this.showModal('bulkDeleteContactsModal', null);
  }

  async performBulkDelete() {
    this.setBusy(this.bulkDeleteSubmitTarget, true);
    try {
      const response = await post(this.bulkDestroyUrlValue, {
        body: this.selectionParams(), contentType: 'application/json', responseKind: 'json'
      });
      const data = await response.json;
      if (response.ok) {
        this.hideModal('bulkDeleteContactsModal');
        this.toast(data.message, data.status === 'partial' ? 'error' : 'success');
        if (data.stats) this.updateStats(data.stats);
        if (data.status === 'partial') {
          this.narrowSelectionTo(data.failed_ids);
          this.refreshTablePreservingSelection();
        } else {
          this.selected.clear();
          this.selectAllMode = false;
          this.dataTable.draw(false);
        }
      } else {
        this.toast((data && data.message) || this.t('action_failed'), 'error');
      }
    } catch {
      this.toast(this.t('action_failed'), 'error');
    } finally {
      this.setBusy(this.bulkDeleteSubmitTarget, false);
    }
  }

  // ==== Helpers =============================================================

  narrowSelectionTo(ids) {
    this.selected = new Set((ids || []).map(String));
    this.selectAllMode = false;
  }

  refreshTablePreservingSelection() {
    this.preserveNextDraw = true;
    this.dataTable.draw(false);
  }

  updateStats(stats) {
    [['total_contacts', 'contacts_count'], ['followers_count', 'followers_count'],
     ['attendees_count', 'attendees_count']].forEach(([key, id]) => {
      const el = document.getElementById(id);
      if (el && stats[key] != null) el.textContent = stats[key];
    });
  }

  setBusy(button, busy) {
    button.disabled = busy;
    button.classList.toggle('bulk-btn-busy', busy);
  }

  showModal(id, returnFocusTo) {
    const el = document.getElementById(id);
    const modal = bootstrap.Modal.getOrCreateInstance(el);
    if (!el.dataset.bulkFocusBound) {
      el.dataset.bulkFocusBound = 'true';
      el.addEventListener('hidden.bs.modal', () => {
        if (this.modalReturnFocus && document.contains(this.modalReturnFocus)) this.modalReturnFocus.focus();
      });
    }
    this.modalReturnFocus = returnFocusTo;
    modal.show();
  }

  hideModal(id) {
    const el = document.getElementById(id);
    const modal = bootstrap.Modal.getInstance(el);
    if (modal) modal.hide();
  }

  // Toast via the app's flash slot, rendered as a design-system toast so it
  // matches the server flash partial (shared/_flash_messages.html.erb) and every
  // other toast: white card + tinted icon tile + sentence + close. We keep the
  // `flash flash-<type>` classes (the components/_toast.scss neutralizer keys off
  // .flash) AND add the sg-toast structure. Inline lucide SVGs mirror the proven
  // client-side set in app/javascript/src/custom.js (no bundled lucide runtime).
  toast(message, type) {
    const flash = document.getElementById('flash');
    if (!flash) return;
    flash.innerHTML = '';

    // Map type → DS toast type + icon, like the flash_toast_style helper. This
    // caller only emits 'error' or (default) 'success'.
    const style = type === 'error'
      ? { type: 'error', icon: 'circle-x' }
      : { type: 'success', icon: 'circle-check' };
    const ICONS = {
      'circle-check': '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
      'circle-x': '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
    };
    const svg = (size, paths) =>
      `<svg class="lucide-icon" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
      `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ` +
      `stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

    const item = document.createElement('div');
    item.className = `sg-toast toast-plain flash flash-${style.type}`;
    item.setAttribute('role', 'status');
    item.setAttribute('aria-live', 'polite');

    const iconSpan = document.createElement('span');
    iconSpan.className = `toast-icon ti-${style.type}`;
    iconSpan.innerHTML = svg(18, ICONS[style.icon]); // static SVG, no user data

    const body = document.createElement('div');
    body.className = 'toast-body';
    const sentence = document.createElement('p');
    sentence.className = 'toast-sentence';
    sentence.textContent = message; // user-facing text — text node, never innerHTML
    body.appendChild(sentence);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'toast-close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = svg(14, '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>');
    close.addEventListener('click', () => {
      flash.innerHTML = '';
      flash.classList.add('d-none');
      clearTimeout(this._toastTimer);
    });

    item.append(iconSpan, body, close);
    flash.appendChild(item);
    flash.classList.remove('d-none');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      flash.innerHTML = '';
      flash.classList.add('d-none');
    }, 5000);
  }
}
